import React, { useState, useEffect, useRef } from "react";
import { Card, Typography, Button, Badge, OutlinedInput, Box, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import MemoryIcon from '@mui/icons-material/Memory';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SettingsIcon from '@mui/icons-material/Settings';
import OpenWithIcon from '@mui/icons-material/OpenWith';
// import CropSquareIcon from '@mui/icons-material/CropSquare';
import MinimizeIcon from '@mui/icons-material/Minimize';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import LocationPinIcon from '@mui/icons-material/LocationOn';
import MapIcon from '@mui/icons-material/Map';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SearchIcon from '@mui/icons-material/Search';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import RefreshIcon from '@mui/icons-material/Refresh';
import Config from '../../../config';
import { formatLocalDateTime, toLocalDate } from '../../../utils/dateTime';
import { useLatestRef } from '../../../utils/hooks';
import {
  ZERO_TELEOP_COMMAND,
  buildRobotControlWebSocketUrl,
  combineTeleopCommand,
  createTeleopPayload,
  getGamepadTeleopCommand,
  getMoveJoystickCommand,
  getTurnJoystickYaw,
  isNeutralTeleopCommand,
} from '../../../utils/teleoperation';

const statusColors = {
  operational: { background: "#10b98122", color: "#10b981", border: "#10b98155" },
  idle: { background: "#64748b22", color: "#64748b", border: "#64748b55" },
  charging: { background: "#3b82f622", color: "#3b82f6", border: "#3b82f655" },
  maintenance: { background: "#f59e0b22", color: "#f59e0b", border: "#f59e0b55" },
  offline: { background: "#ef444422", color: "#ef4444", border: "#ef444455" },
  error: { background: "#ef444422", color: "#ef4444", border: "#ef444455" }
};

const GAMEPAD_DEAD_ZONE = 0.35;
const TELEOP_COMMAND_INTERVAL_MS = 100;
const TELEOP_RECONNECT_MAX_MS = 30000;

function VirtualJoystick({ onChange, horizontalOnly = false, size = 120 }) {
  const [thumbPos, setThumbPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const RADIUS = size / 2 - 12;
  const DEAD_ZONE = 12;

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const updateJoystickFromPointer = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    let dx = e.clientX - (rect.left + rect.width / 2);
    let dy = horizontalOnly ? 0 : e.clientY - (rect.top + rect.height / 2);
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > RADIUS) { dx = (dx / dist) * RADIUS; dy = (dy / dist) * RADIUS; }
    setThumbPos({ x: dx, y: dy });
    onChangeRef.current?.(
      horizontalOnly
        ? getTurnJoystickYaw(dx, RADIUS, DEAD_ZONE)
        : getMoveJoystickCommand(dx, dy, RADIUS, DEAD_ZONE)
    );
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    containerRef.current?.setPointerCapture(e.pointerId);
    updateJoystickFromPointer(e);
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    updateJoystickFromPointer(e);
  };

  const handlePointerUp = () => {
    setIsDragging(false);
    setThumbPos({ x: 0, y: 0 });
    onChangeRef.current?.(horizontalOnly ? 0 : { x: 0, y: 0 });
  };

  return (
    <Box
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      sx={{
        width: size, height: size,
        borderRadius: '50%',
        bgcolor: 'rgba(2,6,23,0.9)',
        border: '2px solid #334155',
        position: 'relative',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        touchAction: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.4)',
      }}
    >
      <Box sx={{ position: 'absolute', width: '75%', height: '1px', bgcolor: '#1e293b' }} />
      {!horizontalOnly && <Box sx={{ position: 'absolute', width: '1px', height: '75%', bgcolor: '#1e293b' }} />}
      <Box sx={{
        position: 'absolute',
        width: 40, height: 40,
        borderRadius: '50%',
        bgcolor: isDragging ? 'rgba(6,182,212,0.6)' : 'rgba(6,182,212,0.2)',
        border: `2px solid ${isDragging ? '#06b6d4' : '#475569'}`,
        transform: `translate(${thumbPos.x}px, ${thumbPos.y}px)`,
        transition: isDragging ? 'none' : 'transform 0.15s ease-out, background-color 0.15s, border-color 0.15s',
        boxShadow: isDragging ? '0 0 14px rgba(6,182,212,0.5)' : 'none',
      }} />
    </Box>
  );
}

export default function ConsolidatedControlPanel({ robot, apiConfig }) {
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMaximized, ] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [robotInfo, setRobotInfo] = useState(null);
  const [robotStatus, setRobotStatus] = useState(null);
  const [mapName, setMapName] = useState("");
  const [, setRobotMaps] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [missions, setMissions] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [missionFilter, setMissionFilter] = useState("");
  const [expandedMissionId, setExpandedMissionId] = useState(null);
  const [expandedScheduleId, setExpandedScheduleId] = useState(null);
  const [schedulingMissionId, setSchedulingMissionId] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ startAt: '', endAt: '', repeat: false, repeatDays: [], repeatUntil: '' });
  const [isRepullingSchedule, setIsRepullingSchedule] = useState(false);
  const [runningMissionId, setRunningMissionId] = useState(null);
  const [pausedMissionId, setPausedMissionId] = useState(null);
  const [confirmStopMissionId, setConfirmStopMissionId] = useState(null);
  const [gamepadControlEnabled, setGamepadControlEnabled] = useState(false);
  const [gamepadName, setGamepadName] = useState("");
  const [gamepadStatus, setGamepadStatus] = useState("Controller off");
  const gamepadAnimationRef = useRef(null);
  const gamepadEnabledRef = useRef(false);
  const sendTeleopCommandRef = useRef(null);
  const setTeleopCommandRef = useRef(null);
  const teleopCommandRef = useRef({ ...ZERO_TELEOP_COMMAND });
  const teleopCommandIntervalRef = useRef(null);
  const virtualMoveRef = useRef({ x: 0, y: 0 });
  const virtualYawRef = useRef(0);
  const getRobotInfoByIdRef = useRef(null);
  const pollGamepadRef = useRef(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!isConnected) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [isConnected]);

  const formatLastSeen = (dateStr) => {
    if (!dateStr) return '—';
    const date = toLocalDate(dateStr);
    if (!date) return '—';
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m ago`;
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setHasDragged(false);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setHasDragged(true);
      setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMoveRef = useLatestRef(handleMouseMove);
  const handleMouseUpRef = useLatestRef(handleMouseUp);

  const handleBubbleClick = () => {
    if (!hasDragged) setIsMinimized(false);
  };

  useEffect(() => {
    if (isDragging) {
      const onMouseMove = (event) => handleMouseMoveRef.current(event);
      const onMouseUp = () => handleMouseUpRef.current();
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      return () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
    }
  }, [isDragging, handleMouseMoveRef, handleMouseUpRef]);

  useEffect(() => {
      if (robot) {
        getRobotInfoByIdRef.current?.(robot);
      }
    }, [robot]);

  const robotControlId = robotInfo?.RobotId;
  const statusWsRef = useRef(null);
  const controlWsRef = useRef(null);
  const controlReconnectTimerRef = useRef(null);
  const controlReconnectAttemptRef = useRef(0);
  const controlReadyRef = useRef(true);

  useEffect(() => {
    if (!robotInfo) return;

    const ws = new WebSocket(Config.robot_status_ws);
    statusWsRef.current = ws;

    ws.onopen = () => console.log("Connected to robot status WS");

    ws.onclose = () => { setIsConnected(false); console.log("Disconnected from robot status WS"); };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "status" && msg.data?.RobotTableId === robotInfo.Id) {
          setIsConnected(true);
          setRobotStatus(msg.data);
        }
      } catch (e) {
        console.error("WS parse error", e);
      }
    };

    ws.onerror = (e) => { setIsConnected(false); console.error("Robot status WS error", e); };

    return () => {
      setIsConnected(false);
      ws.close();
      statusWsRef.current = null;
    };
  }, [robotInfo]);

  useEffect(() => {
    const robotId = robotControlId;
    if (!robotId) return undefined;

    let disposed = false;

    const resetTeleopStream = (requireNeutralRelease = false) => {
      if (
        requireNeutralRelease
        && !isNeutralTeleopCommand(teleopCommandRef.current)
      ) {
        controlReadyRef.current = false;
      }
      teleopCommandRef.current = { ...ZERO_TELEOP_COMMAND };

      if (teleopCommandIntervalRef.current) {
        clearInterval(teleopCommandIntervalRef.current);
        teleopCommandIntervalRef.current = null;
      }
    };

    const connect = () => {
      const websocketUrl = buildRobotControlWebSocketUrl(
        Config.robot_control_ws,
        'controller',
        robotId
      );
      const ws = new WebSocket(websocketUrl);
      controlWsRef.current = ws;

      ws.onopen = () => {
        if (disposed || controlWsRef.current !== ws) return;
        resetTeleopStream(true);
        controlReconnectAttemptRef.current = 0;
        console.log(`Connected to robot control WS for ${robotId}`);
      };

      ws.onclose = () => {
        if (controlWsRef.current === ws) {
          controlWsRef.current = null;
        }
        resetTeleopStream(true);

        if (disposed) return;

        const reconnectDelay = Math.min(
          1000 * (2 ** controlReconnectAttemptRef.current),
          TELEOP_RECONNECT_MAX_MS
        );
        controlReconnectAttemptRef.current += 1;
        controlReconnectTimerRef.current = setTimeout(connect, reconnectDelay);
      };

      ws.onerror = (error) => {
        console.error("Robot control WS error", error);
      };
    };

    connect();

    return () => {
      disposed = true;
      controlReconnectAttemptRef.current = 0;

      if (controlReconnectTimerRef.current) {
        clearTimeout(controlReconnectTimerRef.current);
        controlReconnectTimerRef.current = null;
      }

      const ws = controlWsRef.current;
      if (ws) {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify(createTeleopPayload(
            robotId,
            ZERO_TELEOP_COMMAND
          )));
        }
        ws.close();
        controlWsRef.current = null;
      }

      resetTeleopStream(true);
    };
  }, [robotControlId]);

  const getRobotInfoById = (robot_info) => {
    Promise.all([
      apiConfig.fetchRobotType(robot_info.RobotTypeId),
      apiConfig.fetchRobotSite(robot_info.RobotSiteId),
      apiConfig.fetchRobotSoftwareVersion(robot_info.RobotSoftwareId),
      apiConfig.fetchRobotVersion(robot_info.RobotVersionId),
      apiConfig.fetchRobotStatus(robot_info.Id),
    ]).then(results => {
      robot_info["Type"] = results[0].RobotResponse
      robot_info["Site"] = results[1].RobotResponse.Site
      robot_info["Software"] = results[2].RobotResponse.Version
      robot_info["RobotVersion"] = results[3].RobotResponse.Version
      setRobotInfo(robot_info)
      console.log(robot_info)
      setRobotStatus(results[4].RobotResponse)
      console.log(results[4].RobotResponse)
      getRobotInit(robot_info, results[4].RobotResponse)
    }).catch(error => {
      console.error(error);
    });
  }

  getRobotInfoByIdRef.current = getRobotInfoById;

  const getRobotInit = (robot_info, robot_status) => {
    Promise.all([
        apiConfig.fetchAllMaps(robot_info.Id),
    ]).then(results => {
        const maps = results[0].RobotResponse || [];
        const currentMapId = robot_status?.RobotMapId;
        const currentMap = maps.find(m => String(m.Id) === String(currentMapId));
        setMapName(currentMap?.MapName || "")
        setRobotMaps(maps)
        retrieveMissionsAndSchedules(robot_status?.RobotTableId || robot_info.Id, currentMapId)
    }).catch(error => {
        console.error(error);
    });
  }

  const retrieveMissionsAndSchedules = (robotId, mapId) => {
    const missionRequest = mapId
      ? apiConfig.fetchMissionsByMap(mapId)
      : Promise.resolve({ RobotResponse: [] });

    Promise.all([
      missionRequest,
      apiConfig.fetchScheduleInfo(robotId),
    ]).then(results => {
      console.log(results[0].RobotResponse)
      setMissions(results[0].RobotResponse || [])
      setSchedules(results[1].RobotResponse || [])
    }).catch(error => {
      console.error(error);
    });
  }

  const handleRepullSchedule = () => {
    if (!robotInfo?.RobotId || isRepullingSchedule) return;

    setIsRepullingSchedule(true);
    apiConfig.repullScheduleForRobot(robotInfo.RobotId)
      .then(() => retrieveMissionsAndSchedules(robotInfo.Id, robotStatus?.RobotMapId))
      .catch(console.error)
      .finally(() => setIsRepullingSchedule(false));
  };

  const sendCommand = (action, value = "") => {
    if (!statusWsRef.current || statusWsRef.current.readyState !== WebSocket.OPEN) return;
    statusWsRef.current.send(JSON.stringify({
      action,
      value,
      robot_name: robotInfo?.RobotId,
      instruction: 'movement',
    }));
  };

  const sendTeleopCommand = (value) => {
    if (!robotInfo?.RobotId) return;
    if (!controlWsRef.current || controlWsRef.current.readyState !== WebSocket.OPEN) return;

    controlWsRef.current.send(JSON.stringify(
      createTeleopPayload(robotInfo.RobotId, value)
    ));
  };

  useEffect(() => {
    sendTeleopCommandRef.current = sendTeleopCommand;
  });

  const stopTeleopCommandInterval = () => {
    if (teleopCommandIntervalRef.current) {
      clearInterval(teleopCommandIntervalRef.current);
      teleopCommandIntervalRef.current = null;
    }
  };

  const sendCurrentTeleopCommand = () => {
    sendTeleopCommandRef.current?.({ ...teleopCommandRef.current });
  };

  const setTeleopCommand = (nextCommand) => {
    const combinedCommand = combineTeleopCommand(
      { x: nextCommand?.x, y: nextCommand?.y },
      nextCommand?.yaw
    );

    if (!controlReadyRef.current) {
      if (isNeutralTeleopCommand(combinedCommand)) {
        controlReadyRef.current = true;
      }
      teleopCommandRef.current = { ...ZERO_TELEOP_COMMAND };
      stopTeleopCommandInterval();
      return;
    }

    const wasNeutral = isNeutralTeleopCommand(teleopCommandRef.current);
    teleopCommandRef.current = combinedCommand;
    const isNeutral = isNeutralTeleopCommand(teleopCommandRef.current);

    if (isNeutral) {
      if (!wasNeutral) {
        sendCurrentTeleopCommand();
      }
      stopTeleopCommandInterval();
      return;
    }

    if (wasNeutral) {
      sendCurrentTeleopCommand();
    }

    if (!teleopCommandIntervalRef.current) {
      teleopCommandIntervalRef.current = setInterval(
        sendCurrentTeleopCommand,
        TELEOP_COMMAND_INTERVAL_MS
      );
    }
  };

  setTeleopCommandRef.current = setTeleopCommand;

  useEffect(() => () => {
    if (!isNeutralTeleopCommand(teleopCommandRef.current)) {
      teleopCommandRef.current = { ...ZERO_TELEOP_COMMAND };
      sendTeleopCommandRef.current?.({ ...ZERO_TELEOP_COMMAND });
    }
    if (teleopCommandIntervalRef.current) {
      clearInterval(teleopCommandIntervalRef.current);
      teleopCommandIntervalRef.current = null;
    }
  }, []);

  const handleMoveJoystickChange = (move) => {
    virtualMoveRef.current = move;
    if (gamepadEnabledRef.current) return;

    setTeleopCommandRef.current?.(
      combineTeleopCommand(virtualMoveRef.current, virtualYawRef.current)
    );
  };

  const handleTurnJoystickChange = (yaw) => {
    virtualYawRef.current = yaw;
    if (gamepadEnabledRef.current) return;

    setTeleopCommandRef.current?.(
      combineTeleopCommand(virtualMoveRef.current, virtualYawRef.current)
    );
  };

  const resetVirtualTeleopCommand = () => {
    virtualMoveRef.current = { x: 0, y: 0 };
    virtualYawRef.current = 0;
  };

  const stopGamepadCommandStream = () => {
    setTeleopCommandRef.current?.({ ...ZERO_TELEOP_COMMAND });
  };

  const handleStopTeleoperation = () => {
    resetVirtualTeleopCommand();
    gamepadEnabledRef.current = false;
    controlReadyRef.current = true;
    setGamepadControlEnabled(false);
    if (gamepadAnimationRef.current) {
      cancelAnimationFrame(gamepadAnimationRef.current);
      gamepadAnimationRef.current = null;
    }
    teleopCommandRef.current = { ...ZERO_TELEOP_COMMAND };
    stopTeleopCommandInterval();
    sendTeleopCommandRef.current?.({ ...ZERO_TELEOP_COMMAND });
  };

  const pollGamepad = () => {
    if (!gamepadEnabledRef.current) return;

    if (typeof navigator === 'undefined' || !navigator.getGamepads) {
      setGamepadStatus("Gamepad API unavailable");
      gamepadAnimationRef.current = requestAnimationFrame(() => pollGamepadRef.current?.());
      return;
    }

    const pads = Array.from(navigator.getGamepads()).filter(Boolean);
    const gamepad = pads.find((pad) => pad.connected);

    if (!gamepad) {
      setGamepadName("");
      setGamepadStatus("Press any controller button");
      stopGamepadCommandStream();
      gamepadAnimationRef.current = requestAnimationFrame(() => pollGamepadRef.current?.());
      return;
    }

    setGamepadName(gamepad.id || `Controller ${gamepad.index + 1}`);
    setGamepadStatus("Controller active");

    setTeleopCommandRef.current?.(
      getGamepadTeleopCommand(gamepad, GAMEPAD_DEAD_ZONE)
    );

    gamepadAnimationRef.current = requestAnimationFrame(() => pollGamepadRef.current?.());
  };

  pollGamepadRef.current = pollGamepad;

  const handleToggleGamepadControl = () => {
    resetVirtualTeleopCommand();
    setTeleopCommandRef.current?.({ ...ZERO_TELEOP_COMMAND });

    if (gamepadControlEnabled) {
      gamepadEnabledRef.current = false;
      setGamepadControlEnabled(false);
      setGamepadStatus("Controller off");
      setGamepadName("");
      return;
    }

    setGamepadControlEnabled(true);
    setGamepadStatus("Press any controller button");
  };

  useEffect(() => {
    gamepadEnabledRef.current = gamepadControlEnabled;

    if (gamepadControlEnabled) {
      gamepadAnimationRef.current = requestAnimationFrame(() => pollGamepadRef.current?.());
    } else {
      if (gamepadAnimationRef.current) {
        cancelAnimationFrame(gamepadAnimationRef.current);
        gamepadAnimationRef.current = null;
      }
      stopGamepadCommandStream();
    }

    return () => {
      if (gamepadAnimationRef.current) {
        cancelAnimationFrame(gamepadAnimationRef.current);
        gamepadAnimationRef.current = null;
      }
    };
  }, [gamepadControlEnabled]);

  useEffect(() => {
    const handleGamepadConnected = (event) => {
      if (!gamepadEnabledRef.current) return;
      setGamepadName(event.gamepad.id || `Controller ${event.gamepad.index + 1}`);
      setGamepadStatus("Controller active");
    };

    const handleGamepadDisconnected = () => {
      setGamepadName("");
      setGamepadStatus(gamepadEnabledRef.current ? "Press any controller button" : "Controller off");
      stopGamepadCommandStream();
    };

    window.addEventListener('gamepadconnected', handleGamepadConnected);
    window.addEventListener('gamepaddisconnected', handleGamepadDisconnected);

    return () => {
      window.removeEventListener('gamepadconnected', handleGamepadConnected);
      window.removeEventListener('gamepaddisconnected', handleGamepadDisconnected);
    };
  }, []);

  const handleScheduleMission = (mission) => {
    if (!scheduleForm.startAt || !scheduleForm.endAt) return;
    const resetForm = () => { setSchedulingMissionId(null); setScheduleForm({ startAt: '', endAt: '', repeat: false, repeatDays: [], repeatUntil: '' }); };
    const refreshSchedules = () => {
      resetForm();
      retrieveMissionsAndSchedules(robotInfo?.Id, robotStatus?.RobotMapId);
    };
    const buildPayload = (startISO, endISO) => ({
      RobotMissionId: mission.Id,
      RobotTableId: robotInfo?.Id,
      Status: 'PENDING',
      MissionStartAt: startISO,
      MissionEndAt: endISO,
      ExecutedAt: startISO,
      CompletedAt: endISO,
      Deleted: false,
      CreatedBy: robotInfo?.RobotId || '',
      UpdatedBy: robotInfo?.RobotId || '',
      ScheduledTasklists: mission.Tasklists.map(tl => ({
        MissionTasklistId: tl.Id,
        Status: 'PENDING',
        Deleted: false,
        CreatedBy: robotInfo?.RobotId || '',
        UpdatedBy: robotInfo?.RobotId || '',
        ScheduledTasks: tl.RobotTasklistId.Tasks.map(task => ({
          TasklistTaskId: task.Id,
          Status: 'PENDING',
          Deleted: false,
          CreatedBy: robotInfo?.RobotId || '',
          UpdatedBy: robotInfo?.RobotId || '',
        })),
      })),
    });

    if (!scheduleForm.repeat || scheduleForm.repeatDays.length === 0 || !scheduleForm.repeatUntil) {
      apiConfig.createSchedule(buildPayload(new Date(scheduleForm.startAt).toISOString(), new Date(scheduleForm.endAt).toISOString()))
        .then(refreshSchedules).catch(console.error);
      return;
    }

    const duration = new Date(scheduleForm.endAt) - new Date(scheduleForm.startAt);
    const until = new Date(scheduleForm.repeatUntil);
    until.setHours(23, 59, 59);
    const cursor = new Date(scheduleForm.startAt);
    const payloads = [];

    while (cursor <= until) {
      if (scheduleForm.repeatDays.includes(cursor.getDay())) {
        const s = new Date(cursor);
        const e = new Date(cursor.getTime() + duration);
        payloads.push(buildPayload(s.toISOString(), e.toISOString()));
      }
      cursor.setDate(cursor.getDate() + 1);
    }

    Promise.all(payloads.map(p => apiConfig.createSchedule(p)))
      .then(refreshSchedules).catch(console.error);
  };

  // Minimized Bubble View
  if (isMinimized) {
    return (
      <Box
        sx={{
          position: 'absolute',
          zIndex: 40,
          left: `${position.x}px`,
          top: `${position.y}px`,
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
      >
        <Box
          onMouseDown={handleMouseDown}
          onClick={handleBubbleClick}
          sx={{
            width: 64,
            height: 64,
            background: 'linear-gradient(135deg, #06b6d4, #2563eb)',
            borderRadius: '50%',
            boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'grab',
            '&:hover': { transform: 'scale(1.1)' },
            transition: 'transform 0.2s',
          }}
        >
          <SettingsIcon sx={{ color: 'white', fontSize: '2rem' }} />
        </Box>
      </Box>
    );
  }

  // Maximized or Normal View
  return (
    <Box
      sx={{
        position: 'absolute',
        zIndex: 40,
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: isMaximized ? '480px' : '360px',
        maxHeight: isMaximized ? 'calc(100vh - 150px)' : '400px',
      }}
    >
      <Card sx={{ bgcolor: 'rgba(15,23,42,0.98)', backdropFilter: 'blur(8px)', border: '1px solid #334155', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', overflow: 'hidden' }}>

        {/* Header */}
        <Box
          onMouseDown={handleMouseDown}
          sx={{ bgcolor: '#1e293b', borderBottom: '1px solid #334155', px: 2, py: 1.5, cursor: isDragging ? 'grabbing' : 'grab', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OpenWithIcon sx={{ color: '#64748b', fontSize: '1rem' }} />
            <Typography variant="subtitle2" sx={{ color: 'white' }}>
              Robot Controls
            </Typography>
            <Badge
              variant="outlined"
              sx={{
                borderColor: statusColors[robot.status]?.border,
                bgcolor: statusColors[robot.status]?.background,
                color: statusColors[robot.status]?.color,
                px: 1, py: 0.25, borderRadius: 1, fontSize: '0.7rem',
              }}
            >
              {robot.status}
            </Badge>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setIsMinimized(true)}
              sx={{ color: '#94a3b8', borderColor: '#475569', minWidth: '28px', height: '28px', p: 0, '&:hover': { color: 'white', borderColor: 'white' } }}
            >
              <MinimizeIcon sx={{ fontSize: '0.75rem' }} />
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ overflow: 'auto', maxHeight: isMaximized ? 'calc(100vh - 210px)' : '350px' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ bgcolor: 'rgba(30,41,59,0.5)', borderBottom: '1px solid #334155', minHeight: '36px', '& .MuiTab-root': { color: '#94a3b8', fontSize: '0.75rem', minHeight: '36px', py: 0 }, '& .Mui-selected': { color: 'white', bgcolor: '#334155' }, '& .MuiTabs-indicator': { bgcolor: '#06b6d4' } }}
          >
            <Tab label="Status" />
            <Tab label="Control" />
            <Tab label="Mission" />
            <Tab label="Schedule" />
          </Tabs>

          {/* Status Tab */}
          {activeTab === 0 && robotInfo && robotStatus && (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                  {/* Type */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#94a3b8', fontSize: '0.875rem', minWidth: 80 }}>
                      <SmartToyIcon sx={{ fontSize: '1rem' }} />
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>Robot</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, textAlign: 'right', fontSize: '0.875rem' }}>
                      {robotInfo.Type.Name}
                    </Typography>
                  </Box>
                  {/* Site */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#94a3b8', fontSize: '0.875rem', minWidth: 80 }}>
                      <LocationPinIcon sx={{ fontSize: '1rem' }} />
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>Site</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, textAlign: 'right', fontSize: '0.875rem' }}>
                      {robotInfo.Site}
                    </Typography>
                  </Box>
                  {/* Map */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#94a3b8', fontSize: '0.875rem', minWidth: 80 }}>
                      <MapIcon sx={{ fontSize: '1rem' }} />
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>Map</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, textAlign: 'right', fontSize: '0.875rem' }}>
                      {mapName || "N/A"}
                    </Typography>
                  </Box>
                  {/* Battery */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#94a3b8', fontSize: '0.875rem', minWidth: 80 }}>
                      <BatteryChargingFullIcon sx={{ fontSize: '1rem' }} />
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>Battery</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 96, height: 8, bgcolor: '#020617', borderRadius: 4, overflow: 'hidden' }}>
                        <Box sx={{
                          height: '100%',
                          width: `${robotStatus.BatteryLevel}%`,
                          bgcolor: robotStatus.BatteryLevel > 50 ? '#10b981' : robotStatus.BatteryLevel > 20 ? '#f59e0b' : '#ef4444',
                          transition: 'width 0.3s',
                        }} />
                      </Box>
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, textAlign: 'right', fontSize: '0.875rem' }}>
                        {robotStatus.BatteryLevel}%
                      </Typography>
                    </Box>
                  </Box>
                  {/* Status */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#94a3b8', fontSize: '0.875rem', minWidth: 80 }}>
                      <AssignmentIcon sx={{ fontSize: '1rem' }} />
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>Status</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, textAlign: 'right', fontSize: '0.875rem' }}>
                      {robotStatus.Status || "N/A"}
                    </Typography>
                  </Box>

                  {robot.sensors && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#94a3b8' }}>
                          <DeviceThermostatIcon sx={{ fontSize: '1rem' }} />
                          <Typography variant="body2" sx={{ color: '#94a3b8' }}>CPU Usage</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>{robot.sensors.cpu_usage || 0}%</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#94a3b8' }}>
                          <MemoryIcon sx={{ fontSize: '1rem' }} />
                          <Typography variant="body2" sx={{ color: '#94a3b8' }}>Memory</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>{robot.sensors.memory_usage || 0}%</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#94a3b8' }}>
                          <DeviceThermostatIcon sx={{ fontSize: '1rem' }} />
                          <Typography variant="body2" sx={{ color: '#94a3b8' }}>Temperature</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>{robot.sensors.temperature || 0}°C</Typography>
                      </Box>
                    </>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  {robotInfo.Type.Image && (
                    <Box
                      component="img"
                      src={`data:image/png;base64,${robotInfo.Type.Image}`}
                      alt={robotInfo.RobotId}
                      sx={{
                        width: '100%',
                        maxWidth: 140,
                        maxHeight: 140,
                        objectFit: 'contain',
                        borderRadius: 2,
                        border: '1px solid #1e293b',
                        bgcolor: 'rgba(15,23,42,0.6)',
                        p: 1,
                      }}
                    />
                  )}
                  <Typography variant="caption" sx={{ color: '#64748b', textAlign: 'center' }}>
                    {robotInfo.RobotId}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ borderTop: '1px solid #1e293b', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <Box sx={{ p: 1.5, bgcolor: 'rgba(30,41,59,0.5)', borderRadius: 1 }}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.75rem', mb: 0.5 }}>Missions Completed</Typography>
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.125rem' }}>{robot.total_missions_completed || 0}</Typography>
                </Box>
                <Box sx={{
                  p: 1.5, borderRadius: 1,
                  bgcolor: isConnected ? 'rgba(16,185,129,0.08)' : 'rgba(30,41,59,0.5)',
                  border: '1px solid',
                  borderColor: isConnected ? 'rgba(16,185,129,0.35)' : 'transparent',
                  transition: 'all 0.4s ease',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>Last Seen</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{
                        width: 7, height: 7, borderRadius: '50%',
                        bgcolor: isConnected ? '#10b981' : '#475569',
                        boxShadow: isConnected ? '0 0 6px #10b981' : 'none',
                        '@keyframes blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.2 } },
                        animation: isConnected ? 'blink 1.4s ease-in-out infinite' : 'none',
                        transition: 'background-color 0.3s',
                      }} />
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em', color: isConnected ? '#10b981' : '#475569', transition: 'color 0.3s' }}>
                        {isConnected ? 'LIVE' : 'OFFLINE'}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.125rem' }}>{formatLastSeen(robotStatus?.CreatedAt)}</Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Control Tab */}
          {activeTab === 1 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', mb: 2, fontSize: '0.75rem' }}>
                Manual Teleoperation
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                  <VirtualJoystick onChange={handleMoveJoystickChange} horizontalOnly={false} size={118} />
                  <Typography sx={{ color: '#475569', fontSize: '0.65rem', letterSpacing: '0.05em' }}>MOVE</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Button
                    variant="outlined"
                    onClick={handleToggleGamepadControl}
                    aria-label="Connect game controller"
                    sx={{
                      minWidth: 40,
                      width: 40,
                      height: 40,
                      p: 0,
                      borderRadius: '50%',
                      color: gamepadControlEnabled ? '#06b6d4' : '#94a3b8',
                      borderColor: gamepadControlEnabled ? 'rgba(6,182,212,0.55)' : '#475569',
                      bgcolor: gamepadControlEnabled ? 'rgba(6,182,212,0.14)' : 'rgba(30,41,59,0.45)',
                      '&:hover': {
                        bgcolor: gamepadControlEnabled ? 'rgba(6,182,212,0.22)' : 'rgba(51,65,85,0.7)',
                        borderColor: gamepadControlEnabled ? '#06b6d4' : '#94a3b8',
                      },
                    }}
                  >
                    <SportsEsportsIcon sx={{ fontSize: '1.15rem' }} />
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleStopTeleoperation}
                    sx={{ minWidth: 40, width: 40, height: 40, p: 0, borderRadius: '50%', color: '#f87171', borderColor: 'rgba(239,68,68,0.35)', bgcolor: 'rgba(239,68,68,0.1)', '&:hover': { bgcolor: 'rgba(239,68,68,0.2)', borderColor: '#f87171' } }}
                  >
                    <StopIcon sx={{ fontSize: '1.1rem' }} />
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                  <VirtualJoystick onChange={handleTurnJoystickChange} horizontalOnly={true} size={118} />
                  <Typography sx={{ color: '#475569', fontSize: '0.65rem', letterSpacing: '0.05em' }}>TURN</Typography>
                </Box>
              </Box>
              <Typography
                sx={{
                  color: gamepadControlEnabled ? '#06b6d4' : '#64748b',
                  fontSize: '0.65rem',
                  textAlign: 'center',
                  mt: 1.5,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {gamepadControlEnabled && gamepadName ? gamepadName : gamepadStatus}
              </Typography>
            </Box>
          )}

          {/* Mission Tab */}
          {activeTab === 2 && (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Filter */}
              <OutlinedInput
                fullWidth
                placeholder="Filter missions..."
                value={missionFilter}
                onChange={(e) => setMissionFilter(e.target.value)}
                startAdornment={<SearchIcon sx={{ color: '#64748b', fontSize: '1rem', ml: 0.5, mr: 0.5 }} />}
                sx={{ bgcolor: '#020617', color: 'white', height: '36px', fontSize: '0.875rem', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' } }}
              />

              {/* Mission list */}
              {missions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.875rem' }}>No missions available</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {missions
                    .filter(m => m.Name.toLowerCase().includes(missionFilter.toLowerCase()))
                    .map(mission => {
                      const totalTasks = mission.Tasklists.reduce((sum, tl) => sum + tl.RobotTasklistId.Tasks.length, 0);
                      const isExpanded = expandedMissionId === mission.Id;
                      const taskTypeStyle = (type) => ({
                        waypoint: { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)' },
                        ai:       { color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)' },
                        wait:     { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
                      }[type] || { color: '#64748b', bg: 'rgba(100,116,139,0.12)' });

                      return (
                        <Box key={mission.Id} sx={{ bgcolor: 'rgba(30,41,59,0.5)', borderRadius: 1, border: '1px solid', borderColor: isExpanded ? '#334155' : '#1e293b', transition: 'border-color 0.2s', overflow: 'hidden' }}>
                          {/* Mission header row */}
                          <Box
                            onClick={() => setExpandedMissionId(isExpanded ? null : mission.Id)}
                            sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(51,65,85,0.3)' } }}
                          >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ color: 'white', fontSize: '0.8rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {mission.Name}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                  <FormatListBulletedIcon sx={{ fontSize: '0.7rem', color: '#64748b' }} />
                                  <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>{mission.Tasklists.length} list{mission.Tasklists.length !== 1 ? 's' : ''}</Typography>
                                </Box>
                                <Typography sx={{ color: '#334155', fontSize: '0.7rem' }}>·</Typography>
                                <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>{totalTasks} task{totalTasks !== 1 ? 's' : ''}</Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                              {runningMissionId === mission.Id ? (
                                <>
                                  {pausedMissionId === mission.Id ? (
                                    <Button
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); sendCommand('mission-resume', ""); setPausedMissionId(null); }}
                                      sx={{ bgcolor: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)', fontSize: '0.7rem', height: '28px', px: 1, '&:hover': { bgcolor: 'rgba(16,185,129,0.25)' } }}
                                    >
                                      <PlayArrowIcon sx={{ fontSize: '0.75rem', mr: 0.25 }} />Resume
                                    </Button>
                                  ) : (
                                    <Button
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); sendCommand('mission-pause', ""); setPausedMissionId(mission.Id); }}
                                      sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)', fontSize: '0.7rem', height: '28px', px: 1, '&:hover': { bgcolor: 'rgba(245,158,11,0.25)' } }}
                                    >
                                      <PauseIcon sx={{ fontSize: '0.75rem', mr: 0.25 }} />Pause
                                    </Button>
                                  )}
                                  <Button
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); setConfirmStopMissionId(mission.Id); }}
                                    sx={{ bgcolor: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', fontSize: '0.7rem', height: '28px', px: 1, '&:hover': { bgcolor: 'rgba(239,68,68,0.25)' } }}
                                  >
                                    <StopIcon sx={{ fontSize: '0.75rem', mr: 0.25 }} />Stop
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); sendCommand('startmission', mission.Id); setRunningMissionId(mission.Id); }}
                                  sx={{ bgcolor: 'rgba(6,182,212,0.15)', color: '#06b6d4', border: '1px solid rgba(6,182,212,0.3)', fontSize: '0.7rem', height: '28px', px: 1, '&:hover': { bgcolor: 'rgba(6,182,212,0.25)' } }}
                                >
                                  <PlayArrowIcon sx={{ fontSize: '0.75rem', mr: 0.25 }} />Run
                                </Button>
                              )}
                              <Button
                                size="small"
                                onClick={(e) => { e.stopPropagation(); setSchedulingMissionId(schedulingMissionId === mission.Id ? null : mission.Id); setScheduleForm({ startAt: '', endAt: '' }); }}
                                sx={{ bgcolor: schedulingMissionId === mission.Id ? 'rgba(139,92,246,0.25)' : 'rgba(139,92,246,0.1)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.3)', fontSize: '0.7rem', height: '28px', px: 1, '&:hover': { bgcolor: 'rgba(139,92,246,0.25)' } }}
                              >
                                <EventIcon sx={{ fontSize: '0.75rem', mr: 0.25 }} />Schedule
                              </Button>
                              <KeyboardArrowDownIcon sx={{ color: '#64748b', fontSize: '1rem', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                            </Box>
                          </Box>

                          {/* Inline schedule form */}
                          {schedulingMissionId === mission.Id && (
                            <Box sx={{ borderTop: '1px solid #1e293b', p: 1.5, bgcolor: 'rgba(139,92,246,0.05)', display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Typography sx={{ color: '#a78bfa', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em' }}>SCHEDULE MISSION</Typography>
                              {/* Start / End */}
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography sx={{ color: '#64748b', fontSize: '0.65rem', mb: 0.4 }}>Start</Typography>
                                  <Box component="input" type="datetime-local" value={scheduleForm.startAt}
                                    onChange={(e) => setScheduleForm(f => ({ ...f, startAt: e.target.value }))}
                                    sx={{ width: '100%', bgcolor: '#020617', color: 'white', border: '1px solid #334155', borderRadius: '4px', px: 1, py: 0.6, fontSize: '0.72rem', colorScheme: 'dark', outline: 'none', '&:focus': { borderColor: '#a78bfa' }, boxSizing: 'border-box' }}
                                  />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography sx={{ color: '#64748b', fontSize: '0.65rem', mb: 0.4 }}>End</Typography>
                                  <Box component="input" type="datetime-local" value={scheduleForm.endAt}
                                    onChange={(e) => setScheduleForm(f => ({ ...f, endAt: e.target.value }))}
                                    sx={{ width: '100%', bgcolor: '#020617', color: 'white', border: '1px solid #334155', borderRadius: '4px', px: 1, py: 0.6, fontSize: '0.72rem', colorScheme: 'dark', outline: 'none', '&:focus': { borderColor: '#a78bfa' }, boxSizing: 'border-box' }}
                                  />
                                </Box>
                              </Box>

                              {/* Repeat toggle */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Button size="small"
                                  onClick={() => setScheduleForm(f => ({ ...f, repeat: !f.repeat, repeatDays: [], repeatUntil: '' }))}
                                  sx={{ height: '26px', px: 1.25, fontSize: '0.68rem', border: '1px solid', borderColor: scheduleForm.repeat ? 'rgba(139,92,246,0.5)' : '#334155', color: scheduleForm.repeat ? '#a78bfa' : '#64748b', bgcolor: scheduleForm.repeat ? 'rgba(139,92,246,0.12)' : 'transparent', '&:hover': { borderColor: '#a78bfa', color: '#a78bfa' } }}>
                                  Repeat
                                </Button>
                                {scheduleForm.repeat && (
                                  <Typography sx={{ color: '#475569', fontSize: '0.65rem' }}>Select days it repeats</Typography>
                                )}
                              </Box>

                              {/* Day selector + Until */}
                              {scheduleForm.repeat && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {['Su','Mo','Tu','We','Th','Fr','Sa'].map((d, i) => {
                                      const active = scheduleForm.repeatDays.includes(i);
                                      return (
                                        <Box key={d} onClick={() => setScheduleForm(f => ({ ...f, repeatDays: active ? f.repeatDays.filter(x => x !== i) : [...f.repeatDays, i] }))}
                                          sx={{ flex: 1, textAlign: 'center', py: 0.5, borderRadius: '4px', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 600, border: '1px solid', borderColor: active ? 'rgba(139,92,246,0.5)' : '#334155', color: active ? '#a78bfa' : '#475569', bgcolor: active ? 'rgba(139,92,246,0.12)' : 'transparent', userSelect: 'none', transition: 'all 0.15s' }}>
                                          {d}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                  <Box>
                                    <Typography sx={{ color: '#64748b', fontSize: '0.65rem', mb: 0.4 }}>Repeat until</Typography>
                                    <Box component="input" type="date" value={scheduleForm.repeatUntil}
                                      onChange={(e) => setScheduleForm(f => ({ ...f, repeatUntil: e.target.value }))}
                                      sx={{ width: '100%', bgcolor: '#020617', color: 'white', border: '1px solid #334155', borderRadius: '4px', px: 1, py: 0.6, fontSize: '0.72rem', colorScheme: 'dark', outline: 'none', '&:focus': { borderColor: '#a78bfa' }, boxSizing: 'border-box' }}
                                    />
                                  </Box>
                                </Box>
                              )}

                              {/* Actions */}
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button size="small" onClick={() => { setSchedulingMissionId(null); setScheduleForm({ startAt: '', endAt: '', repeat: false, repeatDays: [], repeatUntil: '' }); }}
                                  sx={{ color: '#64748b', border: '1px solid #334155', fontSize: '0.7rem', height: '28px', px: 1.5, '&:hover': { borderColor: '#94a3b8', color: '#94a3b8' } }}>
                                  Cancel
                                </Button>
                                <Button size="small"
                                  disabled={!scheduleForm.startAt || !scheduleForm.endAt || (scheduleForm.repeat && (scheduleForm.repeatDays.length === 0 || !scheduleForm.repeatUntil))}
                                  onClick={() => handleScheduleMission(mission)}
                                  sx={{ bgcolor: 'rgba(139,92,246,0.2)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.4)', fontSize: '0.7rem', height: '28px', px: 1.5, '&:hover': { bgcolor: 'rgba(139,92,246,0.3)' }, '&.Mui-disabled': { opacity: 0.4 } }}>
                                  Save
                                </Button>
                              </Box>
                            </Box>
                          )}

                          {/* Expanded tasklist/task detail */}
                          {isExpanded && (
                            <Box sx={{ borderTop: '1px solid #1e293b', display: 'flex', flexDirection: 'column', gap: 0 }}>
                              {mission.Tasklists.map((tl, tlIdx) => (
                                <Box key={tl.Id}>
                                  {/* Tasklist header */}
                                  <Box sx={{ px: 1.5, py: 0.75, bgcolor: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', gap: 0.75, borderTop: tlIdx > 0 ? '1px solid #1e293b' : 'none' }}>
                                    <Typography sx={{ color: '#64748b', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em' }}>#{tl.SeqId}</Typography>
                                    <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 600 }}>{tl.RobotTasklistId.Name}</Typography>
                                    <Typography sx={{ color: '#334155', fontSize: '0.65rem', ml: 'auto' }}>{tl.RobotTasklistId.Tasks.length} tasks</Typography>
                                  </Box>
                                  {/* Tasks */}
                                  {tl.RobotTasklistId.Tasks.map((t, tIdx) => {
                                    const style = taskTypeStyle(t.RobotTaskId.Type);
                                    return (
                                      <Box key={t.Id} sx={{ px: 1.5, py: 0.6, display: 'flex', alignItems: 'center', gap: 1, borderTop: '1px solid rgba(30,41,59,0.8)', '&:hover': { bgcolor: 'rgba(30,41,59,0.4)' } }}>
                                        <Typography sx={{ color: '#475569', fontSize: '0.65rem', width: 14, flexShrink: 0 }}>{t.SeqId}</Typography>
                                        <Typography sx={{ color: '#cbd5e1', fontSize: '0.72rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {t.RobotTaskId.Name}
                                        </Typography>
                                        <Box sx={{ px: 0.6, py: 0.1, borderRadius: 0.5, bgcolor: style.bg, flexShrink: 0 }}>
                                          <Typography sx={{ color: style.color, fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            {t.RobotTaskId.Type}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    })
                  }
                  {missions.filter(m => m.Name.toLowerCase().includes(missionFilter.toLowerCase())).length === 0 && (
                    <Typography sx={{ color: '#64748b', fontSize: '0.8rem', textAlign: 'center', py: 2 }}>No missions match "{missionFilter}"</Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Schedule Tab */}
          {activeTab === 3 && (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  Schedules
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleRepullSchedule}
                  disabled={isRepullingSchedule || !robotInfo?.RobotId}
                  sx={{
                    height: 28,
                    minWidth: 34,
                    px: 1,
                    color: '#06b6d4',
                    borderColor: 'rgba(6,182,212,0.35)',
                    bgcolor: 'rgba(6,182,212,0.08)',
                    '&:hover': { borderColor: '#06b6d4', bgcolor: 'rgba(6,182,212,0.15)' },
                    '&.Mui-disabled': { color: '#475569', borderColor: '#334155' },
                  }}
                >
                  <RefreshIcon sx={{ fontSize: '0.9rem', mr: 0.5 }} />
                  <Typography component="span" sx={{ fontSize: '0.65rem', fontWeight: 700 }}>
                    {isRepullingSchedule ? 'Pulling' : 'Repull'}
                  </Typography>
                </Button>
              </Box>
              {schedules.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.875rem' }}>No schedules available</Typography>
                </Box>
              ) : (
                schedules
                  .sort((a, b) => (toLocalDate(b.MissionStartAt)?.getTime() || 0) - (toLocalDate(a.MissionStartAt)?.getTime() || 0))
                  .map(schedule => {
                  const statusMap = {
                    '0': { label: 'Scheduled', color: '#06b6d4', bg: 'rgba(6,182,212,0.12)' },
                    '1': { label: 'Running',   color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
                    '2': { label: 'Completed', color: '#64748b', bg: 'rgba(100,116,139,0.12)' },
                    '3': { label: 'Failed',    color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
                  };
                  const status = statusMap[schedule.Status] || statusMap['0'];
                  const mission = missions.find(m => m.Id === schedule.RobotMissionId);
                  const missionName = mission?.Name || 'Unknown mission';
                  const scheduledTasklists = schedule.ScheduledTasklists || [];
                  const totalTasks = scheduledTasklists.reduce((sum, tl) => sum + (tl.ScheduledTasks?.length || 0), 0);

                  const fmtTime = (iso) => {
                    if (!iso) return '—';
                    return formatLocalDateTime(iso, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                  };

                  const isExpanded = expandedScheduleId === schedule.Id;

                  return (
                    <Box key={schedule.Id} sx={{ bgcolor: 'rgba(30,41,59,0.5)', borderRadius: 1, border: '1px solid', borderColor: isExpanded ? '#334155' : (schedule.Status === '1' ? 'rgba(16,185,129,0.25)' : '#1e293b'), overflow: 'hidden', transition: 'border-color 0.2s' }}>
                      {/* Clickable header */}
                      <Box
                        onClick={() => setExpandedScheduleId(isExpanded ? null : schedule.Id)}
                        sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(51,65,85,0.3)' } }}
                      >
                        {/* Status + counts row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <AccessTimeIcon sx={{ fontSize: '0.85rem', color: status.color, flexShrink: 0 }} />
                            <Box sx={{ px: 0.75, py: 0.1, borderRadius: 0.5, bgcolor: status.bg }}>
                              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em', color: status.color }}>
                                {status.label}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <FormatListBulletedIcon sx={{ fontSize: '0.7rem', color: '#64748b' }} />
                            <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>{scheduledTasklists.length} list{scheduledTasklists.length !== 1 ? 's' : ''}</Typography>
                            <Typography sx={{ color: '#334155', fontSize: '0.7rem' }}>·</Typography>
                            <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>{totalTasks} task{totalTasks !== 1 ? 's' : ''}</Typography>
                            <KeyboardArrowDownIcon sx={{ color: '#64748b', fontSize: '1rem', ml: 0.5, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                          </Box>
                        </Box>

                        {/* Details */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                          {missionName && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ color: '#64748b', fontSize: '0.7rem', minWidth: 56 }}>Mission</Typography>
                              <Typography sx={{ color: '#06b6d4', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{missionName}</Typography>
                            </Box>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ color: '#64748b', fontSize: '0.7rem', minWidth: 56 }}>Start</Typography>
                            <Typography sx={{ color: '#cbd5e1', fontSize: '0.7rem' }}>{fmtTime(schedule.MissionStartAt)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ color: '#64748b', fontSize: '0.7rem', minWidth: 56 }}>End</Typography>
                            <Typography sx={{ color: '#cbd5e1', fontSize: '0.7rem' }}>{fmtTime(schedule.MissionEndAt)}</Typography>
                          </Box>
                          {schedule.ExecutedAt && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ color: '#64748b', fontSize: '0.7rem', minWidth: 56 }}>Executed</Typography>
                              <Typography sx={{ color: '#cbd5e1', fontSize: '0.7rem' }}>{fmtTime(schedule.ExecutedAt)}</Typography>
                            </Box>
                          )}
                          {schedule.CompletedAt && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ color: '#64748b', fontSize: '0.7rem', minWidth: 56 }}>Completed</Typography>
                              <Typography sx={{ color: '#10b981', fontSize: '0.7rem' }}>{fmtTime(schedule.CompletedAt)}</Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Expanded tasklist/task detail */}
                      {isExpanded && (
                        <Box sx={{ borderTop: '1px solid #1e293b' }}>
                          {scheduledTasklists.map((stl, tlIdx) => {
                            const missionTl = mission?.Tasklists.find(tl => tl.Id === stl.MissionTasklistId);
                            const tlStatus = statusMap[stl.Status] || statusMap['0'];
                            return (
                              <Box key={stl.Id}>
                                {/* Tasklist header */}
                                <Box sx={{ px: 1.5, py: 0.75, bgcolor: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: tlIdx > 0 ? '1px solid #1e293b' : 'none' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                    <Typography sx={{ color: '#475569', fontSize: '0.65rem', fontWeight: 700 }}>#{tlIdx}</Typography>
                                    <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {missionTl?.RobotTasklistId.Name || stl.MissionTasklistId}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ px: 0.6, py: 0.1, borderRadius: 0.5, bgcolor: tlStatus.bg, flexShrink: 0, ml: 0.5 }}>
                                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: tlStatus.color }}>{tlStatus.label}</Typography>
                                  </Box>
                                </Box>
                                {/* Tasks */}
                                {(stl.ScheduledTasks || []).map((st) => {
                                  const missionTask = missionTl?.RobotTasklistId.Tasks.find(t => t.Id === st.TasklistTaskId);
                                  const tStatus = statusMap[st.Status] || statusMap['0'];
                                  const taskTypeStyle = {
                                    waypoint: { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)' },
                                    ai:       { color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)' },
                                    wait:     { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
                                  }[missionTask?.RobotTaskId.Type] || { color: '#64748b', bg: 'rgba(100,116,139,0.12)' };

                                  return (
                                    <Box key={st.Id} sx={{ px: 1.5, py: 0.55, display: 'flex', alignItems: 'center', gap: 1, borderTop: '1px solid rgba(30,41,59,0.8)', '&:hover': { bgcolor: 'rgba(30,41,59,0.4)' } }}>
                                      <Typography sx={{ color: '#475569', fontSize: '0.65rem', flexShrink: 0, width: 14 }}>{missionTask?.SeqId ?? ''}</Typography>
                                      <Typography sx={{ color: '#cbd5e1', fontSize: '0.72rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {missionTask?.RobotTaskId.Name || st.TasklistTaskId}
                                      </Typography>
                                      {missionTask && (
                                        <Box sx={{ px: 0.5, py: 0.1, borderRadius: 0.5, bgcolor: taskTypeStyle.bg, flexShrink: 0 }}>
                                          <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: taskTypeStyle.color, textTransform: 'uppercase' }}>
                                            {missionTask.RobotTaskId.Type}
                                          </Typography>
                                        </Box>
                                      )}
                                      <Box sx={{ px: 0.5, py: 0.1, borderRadius: 0.5, bgcolor: tStatus.bg, flexShrink: 0 }}>
                                        <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: tStatus.color }}>{tStatus.label}</Typography>
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  );
                })
              )}
            </Box>
          )}
        </Box>
      </Card>
      <Dialog
        open={confirmStopMissionId !== null}
        onClose={() => setConfirmStopMissionId(null)}
        PaperProps={{ sx: { bgcolor: '#1e293b', border: '1px solid #334155', borderRadius: 2, minWidth: 280 } }}
      >
        <DialogTitle sx={{ color: 'white', fontSize: '0.95rem', pb: 1 }}>Stop Mission</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.875rem' }}>
            Are you sure you want to stop this mission?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            size="small"
            onClick={() => setConfirmStopMissionId(null)}
            sx={{ color: '#64748b', border: '1px solid #334155', fontSize: '0.75rem', '&:hover': { borderColor: '#94a3b8', color: '#94a3b8' } }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={() => {
              sendCommand('mission-stop', "");
              setRunningMissionId(null);
              setPausedMissionId(null);
              setConfirmStopMissionId(null);
            }}
            sx={{ bgcolor: 'rgba(239,68,68,0.2)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.4)', fontSize: '0.75rem', '&:hover': { bgcolor: 'rgba(239,68,68,0.3)' } }}
          >
            Stop
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
